/**
 * The estimate_value type's own report tab: accepted values against what the
 * work actually took (loop two, PRD-value-suggestion-agent §6), the agents
 * beside the people.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DataTable, type Column } from '../../ds';
import { loadValueOutcomes, summarizeValueOutcomes, type AttributeOutcomeSummary, type OriginSummary, type ValueOutcome } from '../../../lib/valueOutcomesService';
import { when, type OutputReviewProps } from './types';

function accuracyCell(s: OriginSummary): string {
  if (s.count === 0) return '—';
  const off = s.meanError == null ? '?' : s.meanError.toFixed(2);
  const exact = s.exactRate == null ? '?' : `${Math.round(s.exactRate * 100)}%`;
  return `${s.count} · off by ${off} · exact ${exact}`;
}

const ACCURACY_COLUMNS: Column<AttributeOutcomeSummary>[] = [
  { key: 'attribute', header: 'Attribute', render: (a) => <span className="font-medium text-[var(--text-pri)]">{a.attributeKey}</span> },
  { key: 'all', header: 'Everyone', render: (a) => accuracyCell(a.all) },
  { key: 'agent', header: 'Agents', render: (a) => accuracyCell(a.agent) },
  { key: 'person', header: 'People', render: (a) => accuracyCell(a.person) },
];

export function EstimateAccuracyTab({ workspaceId, titleOf }: Pick<OutputReviewProps, 'workspaceId' | 'titleOf'>) {
  const [outcomes, setOutcomes] = useState<ValueOutcome[]>([]);
  useEffect(() => { void loadValueOutcomes({ workspaceId }).then(setOutcomes); }, [workspaceId]);
  const accuracy = useMemo(() => summarizeValueOutcomes(outcomes), [outcomes]);
  if (outcomes.length === 0) {
    return (
      <div className="card text-center py-10">
        <p className="text-sm font-medium text-[var(--text-pri)]">Nothing realized yet</p>
        <p className="text-xs text-[var(--text-ter)] mt-1">When an item is delivered, record what it actually took in the Deliver Item modal. Accepted values get scored here, the agents beside the people.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="card overflow-x-auto">
        <DataTable columns={ACCURACY_COLUMNS} rows={accuracy} getRowKey={(a) => a.attributeKey} density="compact" caption="Accepted values against what the work actually took, per attribute" captionHidden />
      </div>
      <div className="card divide-y divide-[var(--rule)]">
        {outcomes.slice(0, 50).map((o) => (
          <div key={o.id} className="py-2 text-xs flex flex-wrap items-center gap-2">
            <Link to={`/prioritization/items/${o.itemId}`} className="font-medium text-[var(--text-pri)] underline">{titleOf(o.itemId)}</Link>
            <span className="text-[var(--text-ter)]">{o.attributeKey}</span>
            <span>accepted <strong>{String(o.acceptedValue ?? '—')}</strong> ({o.acceptedOrigin === 'agent' ? 'an agent' : o.acceptedOrigin === 'person' ? 'a person' : 'no accepted value'})</span>
            <span>· actual <strong>{String(o.realizedValue)}</strong></span>
            {o.error != null && <span className="text-[var(--text-sec)]">· {o.error === 0 ? 'exact' : `off by ${o.error}`}</span>}
            <span className="ml-auto text-[var(--text-ter)]">{when(o.realizedAt)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
